import {
	Button,
	Label,
	Checkbox,
	Popover,
	PopoverTrigger,
	PopoverContent,
	Calendar,
	Select,
	SelectTrigger,
	SelectContent,
	SelectValue,
	SelectItem
} from '@/components/ui';
import { ControlledInput, ControlledTextarea } from '@/components/controlled';
import { DEFAULT_FORM_CONFIG } from '@/constants/formConfig.constant';
import {
	PROMOTION_DESCRIPTION_MAX_LENGTH,
	PROMOTION_PRODUCT_OPTIONS,
	PROMOTION_PRODUCT_PACKAGES,
	PROMOTION_TITLE_MAX_LENGTH
} from '@/constants';
import { useRequestCreatePromotion } from '@/hooks/httpRequest/promotions';
import { type PromotionProductEnum, MessageLanguageEnum } from '@/enums';
import { useForm, type UseFormProps, Controller } from 'react-hook-form';
import { useState } from 'react';
import { notify, formatPromotionPackageValue } from '@/functions';
import { CalendarIcon, ChevronDown, Megaphone, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { LayoutPage } from '@/components/hoc';
import routes from '@/lib/routes';
import type { ICreatePromotionDialogFormData } from '@/interfaces';

const CreatePromotionPage = () => {
	const defaultValues: ICreatePromotionDialogFormData = {
		products: [],
		percentage: '',
		from: undefined,
		to: undefined,
		title: { fr: '', en: '' },
		description: { fr: '', en: '' },
		primaryBtn: {
			text: { fr: '', en: '' }
		}
	};

	const {
		control,
		handleSubmit,
		reset,
		watch,
		formState: { isSubmitting, errors }
	} = useForm<ICreatePromotionDialogFormData>({
		...(DEFAULT_FORM_CONFIG as UseFormProps<ICreatePromotionDialogFormData>),
		defaultValues
	});

	const watchFrom = watch('from');
	const watchProducts = watch('products');
	const watchPercentage = watch('percentage');
	const watchTextFR = watch('primaryBtn.text.fr');
	const watchTextEN = watch('primaryBtn.text.en');
	const watchTitle = watch('title');
	const watchDescription = watch('description');

	const isAnyBtnFieldFilled = !!(watchTextFR || watchTextEN);
	const [isApercuOpen, setIsApercuOpen] = useState(true);
	const [isPrimaryBtnOpen, setIsPrimaryBtnOpen] = useState(false);
	const [descLang, setDescLang] = useState<MessageLanguageEnum>(MessageLanguageEnum.FR);

	const hasLangError =
		descLang === MessageLanguageEnum.FR
			? !!(errors.title?.fr || errors.description?.fr || errors.primaryBtn?.text?.fr)
			: !!(errors.title?.en || errors.description?.en || errors.primaryBtn?.text?.en);

	const { triggerCreatePromotion } = useRequestCreatePromotion({
		onSuccess: () => {
			notify('Promotion créée.');
			reset(defaultValues);
			window.close();
		}
	});

	const onSubmit = handleSubmit(async data => {
		try {
			// the button always opens the purchase of the promoted product, the API picks it
			const primaryBtn = isAnyBtnFieldFilled ? { text: data.primaryBtn.text } : undefined;

			await triggerCreatePromotion({
				products: data.products,
				percentage: data.percentage as number,
				from: data.from!.toISOString(),
				to: data.to!.toISOString(),
				title: data.title,
				description: data.description,
				...(primaryBtn ? { primaryBtn } : {})
			});
		} catch {
			notify('Erreur lors de la création de la promotion.', 'error');
		}
	}, errs => {
		const hasENError = !!(errs.title?.en || errs.description?.en || errs.primaryBtn?.text?.en);
		const hasFRError = !!(errs.title?.fr || errs.description?.fr || errs.primaryBtn?.text?.fr);
		if (descLang === MessageLanguageEnum.FR && hasENError && !hasFRError) setDescLang(MessageLanguageEnum.EN);
		if (descLang === MessageLanguageEnum.EN && hasFRError && !hasENError) setDescLang(MessageLanguageEnum.FR);
	});

	return (
		<LayoutPage
			header={{
				title: 'Créer une promotion',
				description: 'Configurez et publiez une nouvelle promotion in-app.'
			}}
			breadcrumb={{
				title: 'Créer une promotion',
				subMenus: [
					{
						href: routes.nav.marketing.promotions.getHref(),
						title: 'Marketing',
						menus: [{ href: routes.nav.marketing.promotions.getHref(), title: 'Promotions' }]
					}
				]
			}}
			icon={Megaphone}
		>
			<div className="rounded-xl border bg-card p-6 shadow-xs">
				<form onSubmit={onSubmit} className="flex flex-col gap-6">
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
						{/* Configuration Column */}
						<div className="flex flex-col gap-4">
							{/* Language */}
							<div className="flex flex-col items-end gap-1">
								<Select value={descLang} onValueChange={v => setDescLang(v as MessageLanguageEnum)}>
									<SelectTrigger className={cn('w-36 h-8 text-xs', hasLangError && 'border-red-500')}>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value={MessageLanguageEnum.FR}>Français (FR)</SelectItem>
										<SelectItem value={MessageLanguageEnum.EN}>English (EN)</SelectItem>
									</SelectContent>
								</Select>
								{hasLangError && (
									<span className="flex items-center gap-1 text-xs text-red-500">
										<AlertCircle className="h-3 w-3" />
										Champs {descLang === MessageLanguageEnum.FR ? 'FR' : 'EN'} incomplets
									</span>
								)}
							</div>

							{/* Products */}
							<div className="flex flex-col gap-1">
								<Label>
									Produits <span className="text-red-500">*</span>
								</Label>
								<Controller
									name="products"
									control={control}
									rules={{ validate: v => (v && v.length > 0) || 'Sélectionnez au moins un produit' }}
									render={({ field: { value, onChange }, fieldState: { error } }) => (
										<>
											<Popover>
												<PopoverTrigger asChild>
													<Button
														type="button"
														variant="outline"
														className={cn('justify-between font-normal', error && 'border-red-500')}
													>
														<span className={cn(!value?.length && 'text-muted-foreground')}>
															{value?.length
																? PROMOTION_PRODUCT_OPTIONS.filter(o => value.includes(o.value))
																		.map(o => o.label)
																		.join(', ')
																: 'Sélectionner des produits'}
														</span>
														<ChevronDown className="h-4 w-4 opacity-50" />
													</Button>
												</PopoverTrigger>
												<PopoverContent className="w-full p-2" align="start">
													<div className="flex flex-col gap-1">
														{PROMOTION_PRODUCT_OPTIONS.map(opt => (
															<label
																key={opt.value}
																htmlFor={`product-${opt.value}`}
																className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted cursor-pointer text-sm"
															>
																<Checkbox
																	id={`product-${opt.value}`}
																	checked={value?.includes(opt.value)}
																	onCheckedChange={checked => {
																		if (checked) {
																			onChange([...(value ?? []), opt.value]);
																		} else {
																			onChange((value ?? []).filter((v: PromotionProductEnum) => v !== opt.value));
																		}
																	}}
																/>
																{opt.label}
															</label>
														))}
													</div>
												</PopoverContent>
											</Popover>
											{error && <span className="text-xs text-red-500">{error.message}</span>}
										</>
									)}
								/>
							</div>

							{/* Bonus */}
							<div className="flex flex-col gap-1">
								<Label>
									Bonus (%) <span className="text-red-500">*</span>
								</Label>
								<ControlledInput
									name="percentage"
									control={control}
									type="number"
									placeholder="ex: 20"
									rules={{
										required: 'Champ requis',
										min: { value: 1, message: 'Minimum 1' },
										max: { value: 100, message: 'Maximum 100' }
									}}
								/>
							</div>

							{/* From + To */}
							<div className="grid grid-cols-2 gap-3">
								<div className="flex flex-col gap-1">
									<Label>
										Du <span className="text-red-500">*</span>
									</Label>
									<Controller
										name="from"
										control={control}
										rules={{ required: 'Date de début requise' }}
										render={({ field: { value, onChange }, fieldState: { error } }) => (
											<>
												<Popover>
													<PopoverTrigger asChild>
														<Button
															type="button"
															variant="outline"
															className={cn(
																'justify-start text-left font-normal text-xs px-3',
																!value && 'text-muted-foreground',
																error && 'border-red-500'
															)}
														>
															<CalendarIcon className="mr-1.5 h-3.5 w-3.5 shrink-0" />
															{value ? format(value, 'dd/MM/yyyy HH:mm', { locale: fr }) : 'Début'}
														</Button>
													</PopoverTrigger>
													<PopoverContent className="w-auto p-0">
														<Calendar
															mode="single"
															selected={value}
															onSelect={d => {
																if (!d) return onChange(undefined);
																const next = new Date(d);
																if (value) { next.setHours(value.getHours(), value.getMinutes()); }
																onChange(next);
															}}
															autoFocus
															disabled={d => { const today = new Date(); today.setHours(0,0,0,0); return d < today; }}
														/>
														<div className="border-t p-3">
															<input
																type="time"
																className="w-full rounded-md border px-3 py-1.5 text-sm"
																value={value ? format(value, 'HH:mm') : ''}
																onChange={e => {
																	const [h, m] = e.target.value.split(':').map(Number);
																	const next = value ? new Date(value) : new Date();
																	next.setHours(h, m, 0, 0);
																	onChange(next);
																}}
															/>
														</div>
													</PopoverContent>
												</Popover>
												{error && <span className="text-xs text-red-500">{error.message}</span>}
											</>
										)}
									/>
								</div>
								<div className="flex flex-col gap-1">
									<Label>
										Au <span className="text-red-500">*</span>
									</Label>
									<Controller
										name="to"
										control={control}
										rules={{
											required: 'Date de fin requise',
											validate: v => !watchFrom || !v || v > watchFrom || 'Doit être après la date de début'
										}}
										render={({ field: { value, onChange }, fieldState: { error } }) => (
											<>
												<Popover>
													<PopoverTrigger asChild>
														<Button
															type="button"
															variant="outline"
															className={cn(
																'justify-start text-left font-normal text-xs px-3',
																!value && 'text-muted-foreground',
																error && 'border-red-500'
															)}
														>
															<CalendarIcon className="mr-1.5 h-3.5 w-3.5 shrink-0" />
															{value ? format(value, 'dd/MM/yyyy HH:mm', { locale: fr }) : 'Fin'}
														</Button>
													</PopoverTrigger>
													<PopoverContent className="w-auto p-0">
														<Calendar
															mode="single"
															selected={value}
															onSelect={d => {
																if (!d) return onChange(undefined);
																const next = new Date(d);
																if (value) { next.setHours(value.getHours(), value.getMinutes()); }
																onChange(next);
															}}
															autoFocus
															disabled={d => {
																if (!watchFrom) return false;
																const startDay = new Date(watchFrom);
																startDay.setHours(0, 0, 0, 0);
																return d < startDay;
															}}
														/>
														<div className="border-t p-3">
															<input
																type="time"
																className="w-full rounded-md border px-3 py-1.5 text-sm"
																value={value ? format(value, 'HH:mm') : ''}
																onChange={e => {
																	const [h, m] = e.target.value.split(':').map(Number);
																	const next = value ? new Date(value) : new Date();
																	next.setHours(h, m, 0, 0);
																	onChange(next);
																}}
															/>
														</div>
													</PopoverContent>
												</Popover>
												{error && <span className="text-xs text-red-500">{error.message}</span>}
											</>
										)}
									/>
								</div>
							</div>

							{/* Title */}
							<div className="flex flex-col gap-1">
								<div className="flex items-center justify-between">
									<Label>Titre <span className="text-red-500">*</span></Label>
									<span className="text-xs text-muted-foreground">
										{(watchTitle?.[descLang === MessageLanguageEnum.FR ? 'fr' : 'en'] ?? '').length}/{PROMOTION_TITLE_MAX_LENGTH}
									</span>
								</div>
								<ControlledInput
									name="title.fr"
									control={control}
									placeholder="Titre (FR)"
									maxLength={PROMOTION_TITLE_MAX_LENGTH}
									className={cn(descLang !== MessageLanguageEnum.FR && 'hidden')}
									rules={{ required: 'Le titre est requis' }}
								/>
								{descLang === MessageLanguageEnum.FR && errors.title?.fr && (
									<span className="text-xs text-red-500">{errors.title.fr.message}</span>
								)}
								<ControlledInput
									name="title.en"
									control={control}
									placeholder="Title (EN)"
									maxLength={PROMOTION_TITLE_MAX_LENGTH}
									className={cn(descLang !== MessageLanguageEnum.EN && 'hidden')}
									rules={{ required: 'Le titre est requis' }}
								/>
								{descLang === MessageLanguageEnum.EN && errors.title?.en && (
									<span className="text-xs text-red-500">{errors.title.en.message}</span>
								)}
							</div>

							{/* Description */}
							<div className="flex flex-col gap-1">
								<div className="flex items-center justify-between">
									<Label>Description <span className="text-red-500">*</span></Label>
									<span className="text-xs text-muted-foreground">
										{(watchDescription?.[descLang === MessageLanguageEnum.FR ? 'fr' : 'en'] ?? '').length}/{PROMOTION_DESCRIPTION_MAX_LENGTH}
									</span>
								</div>
								<ControlledTextarea
									name="description.fr"
									control={control}
									placeholder="Description (FR)"
									rows={3}
									maxLength={PROMOTION_DESCRIPTION_MAX_LENGTH}
									className={cn('resize-none', descLang !== MessageLanguageEnum.FR && 'hidden')}
									rules={{ required: 'La description est requise' }}
								/>
								{descLang === MessageLanguageEnum.FR && errors.description?.fr && (
									<span className="text-xs text-red-500">{errors.description.fr.message}</span>
								)}
								<ControlledTextarea
									name="description.en"
									control={control}
									placeholder="Description (EN)"
									rows={3}
									maxLength={PROMOTION_DESCRIPTION_MAX_LENGTH}
									className={cn('resize-none', descLang !== MessageLanguageEnum.EN && 'hidden')}
									rules={{ required: 'La description est requise' }}
								/>
								{descLang === MessageLanguageEnum.EN && errors.description?.en && (
									<span className="text-xs text-red-500">{errors.description.en.message}</span>
								)}
							</div>

							{/* Primary button (optional) */}
							<div className="border rounded-xl bg-muted/20 overflow-hidden">
								<button
									type="button"
									onClick={() => setIsPrimaryBtnOpen(v => !v)}
									className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold hover:bg-muted/60 transition-colors"
								>
									<span>Bouton principal (optionnel)</span>
									<ChevronDown className={cn('h-4 w-4 transition-transform', isPrimaryBtnOpen && 'rotate-180')} />
								</button>
								{isPrimaryBtnOpen && (
									<div className="px-4 pb-4 flex flex-col gap-3">
										<p className="text-xs text-muted-foreground">
											Le bouton ouvre l'achat du produit en promotion (les coins en premier).
										</p>
										<ControlledInput
											name="primaryBtn.text.fr"
											control={control}
											placeholder="Texte du bouton (FR)"
											className={cn(descLang !== MessageLanguageEnum.FR && 'hidden')}
											rules={{
												validate: v => {
													if (!isAnyBtnFieldFilled) return true;
													if (watchTextEN) {
														return !!v || 'Requis si le texte anglais est rempli';
													}
													if (descLang === MessageLanguageEnum.FR) {
														return !!v || 'Requis si le bouton est utilisé';
													}
													return true;
												}
											}}
										/>
										<ControlledInput
											name="primaryBtn.text.en"
											control={control}
											placeholder="Button text (EN)"
											className={cn(descLang !== MessageLanguageEnum.EN && 'hidden')}
											rules={{
												validate: v => {
													if (!isAnyBtnFieldFilled) return true;
													if (watchTextFR) {
														return !!v || 'Requis si le texte français est rempli';
													}
													if (descLang === MessageLanguageEnum.EN) {
														return !!v || 'Requis si le bouton est utilisé';
													}
													return true;
												}
											}}
										/>
									</div>
								)}
							</div>
						</div>

						{/* Aperçu Column */}
						<div className="flex flex-col gap-4">
							<div className="rounded-xl border border-dashed bg-muted/40 overflow-hidden">
								<button
									type="button"
									onClick={() => setIsApercuOpen(v => !v)}
									className="w-full flex items-center justify-between px-4 py-3 text-xs font-bold text-muted-foreground uppercase tracking-widest hover:bg-muted/60 transition-colors"
								>
									<span>Aperçu (+{watchPercentage || 'XX'}%)</span>
									<ChevronDown className={cn('h-4 w-4 transition-transform', isApercuOpen && 'rotate-180')} />
								</button>
								{isApercuOpen && (
									<div className="px-4 pb-4 flex flex-col gap-3">
										{watchProducts && watchProducts.length > 0 ? (
											watchProducts
												.filter(p => PROMOTION_PRODUCT_PACKAGES[p])
												.map(p => {
													const pkgs = PROMOTION_PRODUCT_PACKAGES[p]!;
													const pct = Number(watchPercentage) || 0;
													const label = PROMOTION_PRODUCT_OPTIONS.find(o => o.value === p)?.label;
													return (
														<div key={p} className="flex flex-col gap-1.5">
															<p className="text-xs font-semibold">{label}</p>
															<div className="flex flex-wrap gap-2">
																{pkgs.map(pkg => {
																	const newVal = pct > 0 ? pkg.baseValue * (1 + pct / 100) : pkg.baseValue;
																	return (
																		<span
																			key={pkg.name}
																			className="inline-flex items-center gap-1.5 rounded-lg border bg-background px-2.5 py-1 text-xs"
																		>
																			<span className="text-muted-foreground">{pkg.name}</span>
																			{pkg.unit === 'coins' && <><span className="text-muted-foreground/40">·</span><span className="text-muted-foreground/60">{formatPromotionPackageValue(pkg.baseValue, pkg.unit)} 🪙</span></> }
																			<span className="text-muted-foreground/30">➔</span>
																			<span className="font-bold text-primary">
																				{formatPromotionPackageValue(newVal, pkg.unit)}
																				{pkg.unit === 'coins' ? ' 🪙' : ''}
																			</span>
																		</span>
																	);
																})}
															</div>
														</div>
													);
												})
										) : (
											<p className="text-xs text-muted-foreground text-center py-2">
												{watchPercentage && Number(watchPercentage) > 0 ? 'Sélectionnez des produits' : 'Aucune donnée'}
											</p>
										)}
									</div>
								)}
							</div>
						</div>
					</div>

					<div className="flex justify-end gap-3 pt-2">
						<Button type="button" variant="outline" onClick={() => window.close()} disabled={isSubmitting}>
							Annuler
						</Button>
						<Button type="submit" disabled={isSubmitting}>
							{isSubmitting ? 'Création…' : 'Créer'}
						</Button>
					</div>
				</form>
			</div>
		</LayoutPage>
	);
};

export default CreatePromotionPage;
